(() => {
  if (window.TCGWishlist) return;

  const storageKey = 'tcg-mana-vault-wishlist-v1';
  let memoryFallback = [];
  const parse = (value, fallback) => {
    try { return JSON.parse(value); } catch (_) { return fallback; }
  };
  const read = () => {
    try {
      const stored = parse(localStorage.getItem(storageKey), []);
      return Array.isArray(stored) ? stored : [];
    } catch (_) {
      return [...memoryFallback];
    }
  };
  const write = (items) => {
    memoryFallback = [...items];
    try { localStorage.setItem(storageKey, JSON.stringify(items)); } catch (_) { /* Use this page's memory fallback. */ }
    document.dispatchEvent(new CustomEvent('tcg:wishlist-change', { detail: { items } }));
  };
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const currentVariant = (button) => {
    const form = button.closest('form');
    const input = form && form.querySelector('[name="id"]');
    if (!input) return null;
    const option = input.tagName === 'SELECT' ? input.selectedOptions[0] : null;
    return {
      id: input.value,
      title: option ? option.textContent.trim() : button.dataset.variantTitle,
      price: option ? Number(option.dataset.price || button.dataset.priceCents) : Number(button.dataset.priceCents),
      available: option ? !option.disabled : button.dataset.available === 'true'
    };
  };

  const itemFromButton = (button) => {
    const variant = currentVariant(button);
    return {
      productId: String(button.dataset.productId),
      variantId: String((variant && variant.id) || button.dataset.variantId),
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      variantTitle: (variant && variant.title) || button.dataset.variantTitle,
      price: (variant && variant.price) || Number(button.dataset.priceCents),
      available: variant ? variant.available : button.dataset.available === 'true',
      addedAt: new Date().toISOString()
    };
  };

  const has = (productId) => read().some((item) => String(item.productId) === String(productId));
  const syncControls = () => {
    const items = read();
    document.querySelectorAll('[data-tcg-wishlist-count]').forEach((count) => {
      count.textContent = items.length;
      count.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-tcg-wishlist-header-link]').forEach((link) => {
      link.setAttribute('aria-label', `Wishlist, ${items.length} item${items.length === 1 ? '' : 's'}`);
    });
    document.querySelectorAll('[data-tcg-wishlist-button]').forEach((button) => {
      const active = has(button.dataset.productId);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', `${active ? 'Remove from' : 'Add to'} wishlist: ${button.dataset.productTitle}`);
      const label = button.querySelector('[data-tcg-wishlist-label]');
      if (label) label.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
    });
  };

  const toggle = (button) => {
    const item = itemFromButton(button);
    const items = read();
    const index = items.findIndex((entry) => String(entry.productId) === item.productId);
    const wasRemoved = index >= 0;
    if (wasRemoved) items.splice(index, 1);
    else items.push(item);
    write(items);
    document.querySelectorAll('[data-tcg-wishlist-status]').forEach((status) => {
      status.textContent = wasRemoved
        ? `${item.title} was removed from your wishlist.`
        : `${item.title} was added to your wishlist.`;
    });
  };

  const headerLink = (header, path) => Array.from(header.querySelectorAll('a[href]')).find((link) => {
    try {
      const pathname = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') || '/';
      return path === '/cart' ? pathname === '/cart' : pathname.startsWith('/account');
    } catch (_) {
      return false;
    }
  });

  const directChild = (ancestor, descendant) => {
    let child = descendant;
    while (child && child.parentElement !== ancestor) child = child.parentElement;
    return child;
  };

  const sharedAncestor = (first, second, boundary) => {
    let candidate = first.parentElement;
    while (candidate && boundary.contains(candidate)) {
      if (candidate.contains(second)) return candidate;
      candidate = candidate.parentElement;
    }
    return null;
  };

  const moveHeaderLink = () => {
    const link = document.querySelector('[data-tcg-wishlist-header-link]');
    if (!link) return false;

    const header = document.querySelector(
      'header, [role="banner"], #shopify-section-header, [id^="shopify-section-sections--"][id$="__header"]'
    );
    if (!header) return false;

    if (header.contains(link)) {
      link.classList.add('is-mounted');
      return true;
    }

    const cart = headerLink(header, '/cart');
    const account = headerLink(header, '/account');
    let target = document.querySelector(
      '.header__icons, [data-header-icons], header .site-header__icons, header .header-icons, header [class*="header-actions"], header [class*="header__actions"]'
    );
    let reference = target && cart && target.contains(cart) ? directChild(target, cart) : null;

    if (account && cart) {
      const common = sharedAncestor(account, cart, header);
      if (common) {
        const accountSlot = directChild(common, account);
        const cartSlot = directChild(common, cart);
        if (accountSlot && cartSlot && accountSlot !== cartSlot) {
          target = common;
          reference = cartSlot;
        } else if (cart.parentElement) {
          target = cart.parentElement;
          reference = cart;
        }
      }
    } else if (cart && cart.parentElement) {
      target = cart.parentElement;
      reference = cart;
    }

    if (!target) return false;
    if (link.parentElement !== target || (reference && link.nextElementSibling !== reference)) {
      target.insertBefore(link, reference || null);
    }
    link.classList.add('is-mounted');
    return true;
  };

  const money = (cents, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'NZD'
  }).format(Number(cents || 0) / 100);

  const refreshItem = async (item) => {
    try {
      const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return item;
      const product = await response.json();
      const variant = product.variants.find((entry) => String(entry.id) === String(item.variantId))
        || product.variants.find((entry) => entry.available)
        || product.variants[0];
      return {
        ...item,
        title: product.title,
        url: product.url || item.url,
        image: product.featured_image || item.image,
        variantId: String(variant.id),
        variantTitle: variant.title,
        price: variant.price,
        available: Boolean(variant.available)
      };
    } catch (_) {
      return item;
    }
  };

  const renderItems = (grid, empty, items, currency) => {
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    grid.innerHTML = items.map((item) => `
      <article class="tcg-wishlist-item" data-wishlist-product-id="${escapeHtml(item.productId)}">
        <a class="tcg-wishlist-item__image" href="${escapeHtml(item.url)}"><img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy"></a>
        <div class="tcg-wishlist-item__content">
          <a class="tcg-wishlist-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          <span class="tcg-wishlist-item__variant">${escapeHtml(item.variantTitle === 'Default Title' ? '' : item.variantTitle)}</span>
          <div class="tcg-wishlist-item__price-stock">
            <strong>${money(item.price, currency)}</strong>
            <span class="tcg-wishlist-item__stock${item.available ? '' : ' is-sold-out'}">${item.available ? 'In stock' : 'Sold out'}</span>
          </div>
          <div class="tcg-wishlist-item__actions">
            <button type="button" class="tcg-wishlist-item__add" data-wishlist-add data-variant-id="${escapeHtml(item.variantId)}" ${item.available ? '' : 'disabled'}>${item.available ? 'Add to cart' : 'Sold out'}</button>
            <button type="button" class="tcg-wishlist-item__remove" data-wishlist-remove aria-label="Remove ${escapeHtml(item.title)} from wishlist">Remove</button>
          </div>
        </div>
      </article>`).join('');
  };

  const renderPage = async (root) => {
    if (root.dataset.wishlistInitialized === 'true') return;
    root.dataset.wishlistInitialized = 'true';
    const grid = root.querySelector('[data-tcg-wishlist-grid]');
    const empty = root.querySelector('[data-tcg-wishlist-empty]');
    const message = root.querySelector('[data-tcg-wishlist-message]');
    const currency = root.dataset.currency || 'NZD';
    let items = read();

    // Render saved product data immediately; fresh prices and availability can arrive in the background.
    renderItems(grid, empty, items, currency);

    grid.addEventListener('click', async (event) => {
      const card = event.target.closest('[data-wishlist-product-id]');
      if (!card) return;
      if (event.target.closest('[data-wishlist-remove]')) {
        items = items.filter((item) => String(item.productId) !== card.dataset.wishlistProductId);
        write(items);
        card.remove();
        empty.hidden = items.length > 0;
        grid.hidden = items.length === 0;
        return;
      }
      const add = event.target.closest('[data-wishlist-add]');
      if (!add) return;
      add.disabled = true;
      try {
        const response = await fetch(root.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(add.dataset.variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Cart request failed');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        message.textContent = 'Added to your cart.';
      } catch (_) {
        message.textContent = 'This card could not be added. Please try again.';
      } finally {
        add.disabled = false;
      }
    });

    if (items.length === 0) return;

    const refreshedItems = await Promise.all(items.map(refreshItem));
    const refreshedByProduct = new Map(
      refreshedItems.map((item) => [String(item.productId), item])
    );
    items = read().map((item) => refreshedByProduct.get(String(item.productId)) || item);
    write(items);
    renderItems(grid, empty, items, currency);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tcg-wishlist-button]');
    if (button) toggle(button);
  });
  document.addEventListener('tcg:wishlist-change', syncControls);
  const initialize = () => {
    if (!moveHeaderLink()) {
      const observer = new MutationObserver(() => {
        if (moveHeaderLink()) observer.disconnect();
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 10000);
    }
    syncControls();
    document.querySelectorAll('[data-tcg-wishlist-page]').forEach(renderPage);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();

  window.TCGWishlist = { read, write, has };
})();
